<template>
  <section class="ni-section ni-int" id="int-communication">
    <div class="ni-container">
      <SectionHeader
        variant="dark"
        :subtitle="lang === 'zh' ? 'THE WORLD IS IN QINGDAO' : 'THE WORLD IS IN QINGDAO'"
        more-href="/publish"
        :lang="lang"
      >
        <template #title>
          <span class="ni-section-header__title-mark">
            {{ lang === 'zh' ? '国际传播' : 'Global Outreach' }}
          </span>
        </template>
      </SectionHeader>

      <div class="ni-int__toolbar">
        <div class="ni-int__toolbar-left">
          <div class="ni-int__cats">
            <div class="ni-int__cat-accent" aria-hidden="true">›</div>
            <div class="ni-int__cat-switch">
            <button
              id="int-cat-world-in-qd"
              type="button"
              class="ni-int__cat-btn"
              :class="{ 'ni-int__cat-btn--active': activeCategory === '世界在青岛' }"
              @click="handleCategorySwitch('世界在青岛')"
            >
              {{ lang === 'zh' ? '世界在青岛' : 'The World in Qingdao' }}
            </button>
            <span class="ni-int__cat-sep">/</span>
            <button
              id="int-cat-qd-in-world"
              type="button"
              class="ni-int__cat-btn"
              :class="{ 'ni-int__cat-btn--active': activeCategory === '青岛在世界' }"
              @click="handleCategorySwitch('青岛在世界')"
            >
              {{ lang === 'zh' ? '青岛在世界' : 'Qingdao in the World' }}
            </button>
          </div>
          </div>
          <MoreLink href="#photo-depot" :lang="lang" />
        </div>

        <div class="ni-int__filters">
          <template v-for="(lbl, idx) in filterTabs" :key="lbl">
            <span v-if="idx > 0" class="ni-int__filter-sep">/</span>
            <button
              :id="`int-filter-btn-${idx}`"
              type="button"
              class="ni-int__filter-btn"
              :class="{ 'ni-int__filter-btn--active': activeFilter === lbl }"
              @click="activeFilter = lbl"
            >
              {{ lbl }}
            </button>
          </template>
        </div>
      </div>

      <div class="ni-int__grid">
        <div class="ni-int__video">
          <div class="ni-int__video-bg">
            <Transition name="ni-int-fade" mode="out-in">
              <img
                :key="activeMedia.id"
                :src="videoBackdropUrl"
                :alt="activeMedia.title"
              />
            </Transition>
            <div class="ni-int__video-vignette"></div>
          </div>

          <div class="ni-int__video-info">
            <Transition name="ni-int-fade" mode="out-in">
              <div :key="activeMedia.id" class="ni-int__video-copy">
                <template v-if="activeMedia.id === 'int-1'">
                  <h3 class="ni-int__video-title">从"青春"出发 为青岛而战</h3>
                  <p class="ni-int__video-summary ni-int__video-summary--hero">
                    {{ lang === 'zh' ? '我们，准备好了！' : 'We are ready!' }}
                  </p>
                </template>
                <template v-else>
                  <h3 class="ni-int__video-title">{{ activeMedia.title }}</h3>
                  <p class="ni-int__video-summary">{{ activeMedia.summary }}</p>
                </template>
              </div>
            </Transition>
          </div>

          <div class="ni-int__controls">
            <button
              id="int-play-button-controller"
              type="button"
              @click="togglePlay"
            >
              <span v-if="isPlaying" aria-hidden="true">❚❚</span>
              <span v-else aria-hidden="true">▶</span>
            </button>
            <span>
              {{ isPlaying ? `0:${elapsedSeconds}` : '0:00' }}
            </span>
            <div class="ni-int__progress">
              <div
                class="ni-int__progress-bar"
                :style="{ width: `${videoProgress}%` }"
              ></div>
            </div>
            <span class="ni-int__duration">{{ activeMedia.videoDuration }}</span>
          </div>
        </div>

        <div class="ni-int__cards">
          <article
            v-for="(item, index) in currentItems"
            :id="`int-right-list-card-${item.id}`"
            :key="item.id"
            class="ni-int__card"
            :class="{ 'ni-int__card--active': index === activeIdx }"
            @click="handleCardClick(index)"
          >
            <div class="ni-int__card-thumb">
              <img :src="item.imageUrl" :alt="item.title" />
            </div>
            <div class="ni-int__card-body">
              <span class="ni-int__card-tag">{{ item.tag }}</span>
              <h4 class="ni-int__card-title">{{ item.title }}</h4>
              <p class="ni-int__card-summary">{{ item.summary }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  getWorldInQingdaoItems,
  getQingdaoInWorldItems
} from '@/data/newIndexMock.js';
import SectionHeader from './shared/SectionHeader.vue';
import MoreLink from './shared/MoreLink.vue';

const props = defineProps({
  lang: {
    type: String,
    default: 'zh',
    validator: (value) => ['zh', 'en'].includes(value)
  }
});

const INT1_HERO_IMAGE =
  'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&h=800&q=80';

const activeCategory = ref('世界在青岛');
const activeFilter = ref(
  props.lang === 'zh' ? '在青外籍人士故事' : 'Expat Stories'
);
const activeIdx = ref(0);
const isPlaying = ref(false);
const videoProgress = ref(0);
let progressTimer = null;

const filterTabs = computed(() =>
  props.lang === 'zh'
    ? ['在青外籍人士故事', '青岛企业出海记', '全球媒体看青岛', '青岛与友城动态']
    : ['Expat Stories', 'Enterprise Sailing', 'Global PR Coverage', 'Sister Cities']
);

const worldInQingdaoItems = computed(() => getWorldInQingdaoItems(props.lang));
const qingdaoInWorldItems = computed(() => getQingdaoInWorldItems(props.lang));

const currentItems = computed(() =>
  activeCategory.value === '世界在青岛'
    ? worldInQingdaoItems.value
    : qingdaoInWorldItems.value
);

const activeMedia = computed(
  () => currentItems.value[activeIdx.value] || currentItems.value[0]
);

const videoBackdropUrl = computed(() =>
  activeMedia.value?.id === 'int-1' ? INT1_HERO_IMAGE : activeMedia.value?.imageUrl
);

const elapsedSeconds = computed(() =>
  Math.floor((videoProgress.value / 100) * 15)
    .toString()
    .padStart(2, '0')
);

function stopProgressTimer() {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

function startProgressTimer() {
  stopProgressTimer();
  progressTimer = setInterval(() => {
    if (videoProgress.value >= 100) {
      isPlaying.value = false;
      videoProgress.value = 0;
      stopProgressTimer();
      return;
    }
    videoProgress.value += 1;
  }, 300);
}

function togglePlay() {
  isPlaying.value = !isPlaying.value;
}

function handleCardClick(index) {
  activeIdx.value = index;
  isPlaying.value = true;
  videoProgress.value = 0;
}

function handleCategorySwitch(category) {
  activeCategory.value = category;
  activeIdx.value = 0;
  isPlaying.value = false;
  videoProgress.value = 0;
}

watch(isPlaying, (playing) => {
  if (playing) {
    startProgressTimer();
  } else {
    stopProgressTimer();
  }
});

watch(
  () => props.lang,
  (lang) => {
    activeFilter.value =
      lang === 'zh' ? '在青外籍人士故事' : 'Expat Stories';
  }
);

onBeforeUnmount(() => {
  stopProgressTimer();
});
</script>
